import { Box, Button, Grid, Typography, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { FormProvider, RHFTextField, RHFDateRangePickerField } from "../../../common/hook-form"
import Loader from "../../../common/Loader"
import { formatCurrency, formatDate, maskAddress, maskPhoneNumber, parseDateAndTime, shouldMaskData, shouldMaskDataPD } from "../../../helpers/UtilityHelper"
import withLayout from "../../../hocs/Layout"
import { useLazyGetPaginatedShipmentsQuery } from "../../../redux/commonRTK"
import _ from "lodash"
import ProzoDataGrid from "../../../common/ProzoDataGrid"
import { exclude } from "./index"
import PageTitle from "../../../common/PageTitle"
import { CSVLink } from "react-csv"
import { getMerchant, getToken } from "../../../helpers/ReduxHelpers"
import { useSelector } from "react-redux"
import jwt_decode from "jwt-decode"
import SearchIcon from '@mui/icons-material/Search'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography'
import { NAVY, SUBTLE, PRIMARY_BLUE, ERROR, BORDER, WHITE } from '../../../styles/foundation/colors'
import {
    pageContainerSx,
    filterBarSx, primaryBtnSx, secondaryBtnSx,
    helpTextSx, helpTextTypeSx, dataGridWrapSx, btnRowSx,
} from '../../../styles/patterns'

const excludes = exclude + ",courierId,shipmentDetail,customerDetail"

// Mirrors backendcode's FailureCategoryEnum (order/src/main/java/com/prozo/proship/enums/
// FailureCategoryEnum.java) -- the order document already carries a classified
// `failureCategory` code (set server-side by OrderFailureClassifier); this just gives it a
// seller-facing label + explanation instead of the raw rule-engine/courier-API remark string.
const FAILURE_CATEGORY_INFO = {
    PINCODE_NOT_SERVICEABLE: { label: 'Pincode Not Serviceable', detail: 'Pickup/drop pincode not serviceable by any mapped or recommended courier' },
    PICKUP_PIN_NOT_SERVICEABLE: { label: 'Pickup PIN Not Serviceable', detail: 'Pickup pincode excluded from the mapped allocation rule' },
    DROP_PIN_NOT_SERVICEABLE: { label: 'Drop Pincode Not Serviceable', detail: 'Drop pincode not in any mapped rule / serviceability file' },
    ZONE_NOT_COVERED: { label: 'Destination Zone Not Covered', detail: 'Destination zone not covered by any mapped rule' },
    DELIVERY_TYPE_MISMATCH: { label: 'Delivery Type Mismatch', detail: 'Delivery-type rule condition not met' },
    PAYMENT_MODE_MISMATCH: { label: 'Payment Mode Mismatch', detail: 'Payment-mode rule condition not met (prepaid / COD)' },
    WEIGHT_RULE_FAILED: { label: 'Weight Rule Failed', detail: "Order weight outside the rule's weight slab" },
    FULL_COVERAGE_ABSENT: { label: 'No Courier Covers Pincode', detail: 'No courier covers this pincode in any serviceability file' },
    ALLOCATION_NO_RULE: { label: 'Allocation — No Rule Match', detail: 'No allocation rule matched this order' },
    COURIER_API_NOT_SERVICEABLE: { label: 'Courier — Pincode Not Serviceable', detail: 'Courier API reported the pincode as non-serviceable' },
    COURIER_API_ERROR: { label: 'Courier API Error', detail: 'Courier rejected the order at API level' },
    ADMIN_PIN_BLOCKED: { label: 'Courier — PIN Blocked', detail: 'Drop/Pickup PIN blocked in courier config at Prozo level' },
    WALLET_INSUFFICIENT: { label: 'Wallet Limit Exhausted', detail: 'Insufficient funds or credit limit exceeded on the merchant account' },
    DATA_QUALITY_FAILURE: { label: 'Data Quality — Dimensions / Weight', detail: 'Missing or invalid order dimensions / weight' },
    PARTIAL_FAILURE: { label: 'Partial — Some Couriers Succeeded', detail: 'Some couriers failed but at least one succeeded' },
    UNCLASSIFIED: { label: 'Unknown / Other', detail: 'Unclassified error — contact support with the reference number' },
}
const failureReasonLabel = (row) => FAILURE_CATEGORY_INFO[row?.failureCategory]?.label || row?.failureCategory || 'Unclassified'

const ORDER_TYPES = [
    { value: 'all', label: 'All' },
    { value: 'FORWARD SHIPMENT', label: 'Forward' },
    { value: 'REVERSE SHIPMENT', label: 'Reverse' },
]

// Reason cell: the red plain-language failure label is always shown; the long raw system
// remark is tucked behind a "Details" toggle so it doesn't bloat every row's height.
function ReasonCell({ row }) {
    const [open, setOpen] = useState(false)
    const info = FAILURE_CATEGORY_INFO[row?.failureCategory]
    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Tooltip title={info?.detail || row.remark || ''} arrow placement="top">
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: ERROR, lineHeight: 1.5, cursor: 'help', textDecoration: 'underline dotted' }}>
                        {failureReasonLabel(row)}
                    </Typography>
                </Tooltip>
                {row.remark && (
                    <Box
                        onClick={() => setOpen((o) => !o)}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2, cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.medium, color: SUBTLE }}>
                            {open ? 'Hide details' : 'Details'}
                        </Typography>
                        {open
                            ? <ExpandLessIcon sx={{ fontSize: 14, color: SUBTLE }} />
                            : <ExpandMoreIcon sx={{ fontSize: 14, color: SUBTLE }} />}
                    </Box>
                )}
            </Box>
            {row.remark && open && (
                <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.4, mt: 0.3, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {row.remark}
                </Typography>
            )}
        </Box>
    )
}

function StuckOrders() {
    const [page, setPage] = useState(0)
    const [sizePerPage, setSizePerPage] = useState(10)
    const [totalRows, setTotalRows] = useState(0)
    const [formFilters, setFormFilters] = useState([])
    // Forward vs RVP bifurcation -- a separate toggle (not part of the text-field filter form)
    // so switching it applies immediately, same pattern as the ABD/Non-ABD toggle on the PTL
    // shipments list.
    const [orderTypeFilter, setOrderTypeFilter] = useState('all')
    const [exportData, setExportData] = useState([])
    const [isExporting, setIsExporting] = useState(false)
    const csvLink = useRef(null)
    const [getPaginatedShipments, { data, isLoading, isFetching }] = useLazyGetPaginatedShipmentsQuery()
    const merchant = getMerchant()
    const merchantId = merchant.id ? merchant.id : ''
    const { report } = useSelector(state => state)
    const { piiAccessAllow } = jwt_decode(getToken())

    const defaultFilters = [
        { field: "orderSubStatus", operator: "eq", value: "UNPROCESSED" },
        { field: "merchant.$id", operator: "eq", value: merchantId }
    ]

    const andFilters = useMemo(() => {
        const combined = [...formFilters]
        if (orderTypeFilter !== 'all') combined.push({ field: "orderType", operator: "eq", value: orderTypeFilter })
        return combined
    }, [formFilters, orderTypeFilter])

    const csvHeaders = [
        { label: "Reference Id", key: "reference" },
        { label: "Order Id", key: "orderId" },
        { label: "Created Date", key: "createdDate" },
        { label: "Order Type", key: "order_type" },
        { label: "Invoice Value", key: "invoice_value" },
        { label: "Payment Mode", key: "payment_mode" },
        { label: "Pickup Pincode", key: "pickup_details.from_pincode" },
        { label: "Delivery Pincode", key: "delivery_details.to_pincode" },
        { label: "Failure Reason", key: "failureReasonLabel" },
        { label: "Raw Remark", key: "remark" },
    ]

    useEffect(() => {
        if (data?.count && data?.data) setTotalRows(data.count)
    }, [data])

    useEffect(() => { setPage(0) }, [sizePerPage])

    useEffect(() => {
        getPaginatedShipments({
            offset: page * sizePerPage, limit: sizePerPage,
            andfilter: [...defaultFilters, ...andFilters], excludes
        })
        // eslint-disable-next-line
    }, [page, andFilters])

    const methods = useForm({ defaultValues: { reference: '', pickup_pincode: '', drop_pincode: '', fromDate: '', toDate: '' } })
    const { handleSubmit, reset } = methods

    const applyFilters = (filters) => {
        let applied = []
        if (filters.reference) applied.push({ field: "reference", operator: "eq", value: filters.reference })
        if (filters.pickup_pincode) applied.push({ field: "pickupDetails.from_pincode", operator: "eq", value: filters.pickup_pincode })
        if (filters.drop_pincode) applied.push({ field: "deliveryDetails.to_pincode", operator: "eq", value: filters.drop_pincode })
        // Scopes the on-screen list AND the export -- previously neither had any date bound,
        // which is what made the export effectively cover the shipment's entire history.
        if (filters.fromDate) applied.push({ field: "createdDate", operator: "gte", value: `Date('${formatDate(filters.fromDate, 'yyyy-MM-dd')} 00:00:00')` })
        if (filters.toDate) applied.push({ field: "createdDate", operator: "lte", value: `Date('${formatDate(filters.toDate, 'yyyy-MM-dd')} 23:59:59')` })
        setFormFilters(applied)
    }

    // Plain RHF reset() only clears the form fields visually -- it never touched
    // `formFilters`, so the table kept showing the old query even after the fields looked
    // cleared. Clear the applied filters too so Reset actually refetches the unfiltered list.
    const onReset = () => {
        reset()
        setFormFilters([])
        setOrderTypeFilter('all')
    }

    const fetchAllDataForExport = async () => {
        setIsExporting(true)
        const pageSize = 200
        let allData = [], offset = 0
        while (true) {
            const response = await getPaginatedShipments({
                offset, limit: pageSize,
                andfilter: [...defaultFilters, ...andFilters], excludes
            }).unwrap()
            allData = allData.concat(response.data)
            if (response.data.length < pageSize) break
            offset += pageSize
        }
        setExportData(allData.map((row) => ({ ...row, failureReasonLabel: failureReasonLabel(row) })))
        setIsExporting(false)
        setTimeout(() => csvLink.current.link.click(), 0)
    }

    const columns = [
        {
            field: 'actions', headerAlign: "left", hideSortIcons: true, headerName: 'Order Details', flex: 1, minWidth: 190,
            renderCell: (params) => {
                const { row } = params
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 1 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                            <strong>Ref:</strong> {row?.reference}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            <strong>Order:</strong> {row?.orderId}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            <strong>Label:</strong> {row?.shippingLabelId}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            {row.createdDate ? parseDateAndTime(row.createdDate) : ""}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                            <strong>Invoice:</strong> {row.invoice_value ? formatCurrency(row.invoice_value) : "N/A"}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                            <strong>Payment:</strong> {row.payment_mode || ''}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            <strong>Units:</strong> {_.sumBy(row.item_list, 'units')}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'pickup', headerAlign: "left", hideSortIcons: true, headerName: 'Pickup & Delivery', flex: 1.8, minWidth: 300,
            renderCell: (params) => {
                const { row } = params
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, py: 1 }}>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY }}>Pickup</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>{row.pickup_details.from_name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            {shouldMaskDataPD(report, row, piiAccessAllow) ? maskAddress(
                                row.pickup_details.from_address, row.pickup_details.from_city,
                                row.pickup_details.from_state, row.pickup_details.from_pincode
                            ) : (
                                <>{row.pickup_details.from_address}, {row.pickup_details.from_city}, {row.pickup_details.from_state}, <span style={{ color: PRIMARY_BLUE, fontWeight: 600 }}>{row.pickup_details.from_pincode}</span></>
                            )}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: NAVY, mt: 0.5 }}>Delivery</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>{row.delivery_details.to_name}</Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            Phone: {shouldMaskData(report, row, piiAccessAllow) ? maskPhoneNumber(row.delivery_details.to_phone_number) : row.delivery_details.to_phone_number}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                            {shouldMaskData(report, row, piiAccessAllow) ? maskAddress(
                                row.delivery_details.to_address, row.delivery_details.to_city,
                                row.delivery_details.to_state, row.delivery_details.to_pincode
                            ) : (
                                <>{row.delivery_details.to_address}, {row.delivery_details.to_city}, {row.delivery_details.to_state}, <span style={{ color: PRIMARY_BLUE, fontWeight: 600 }}>{row.delivery_details.to_pincode}</span></>
                            )}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'remark', headerAlign: "left", hideSortIcons: true, headerName: 'Reason', flex: 0.8, minWidth: 160,
            renderCell: (params) => <ReasonCell row={params.row} />,
        },
    ]

    return (
        <Box sx={pageContainerSx}>
            <PageTitle subTitle="Shipments that could not be processed — review the reason and resolve issues">Unprocessed Shipments</PageTitle>

            {/* Help text */}
            <Box sx={helpTextSx}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
                <Typography sx={helpTextTypeSx}>
                    <strong>What are unprocessed shipments?</strong> These are orders where the system could not assign a courier
                    or process the shipment due to missing data, serviceability issues, or rule conflicts.
                    The Reason column gives a plain-language cause — hover it for more detail, or click "Details" to see the raw system remark.
                </Typography>
            </Box>

            {/* Filter bar */}
            <Box data-rp-filters sx={filterBarSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.semibold, color: SUBTLE }}>
                        Shipment Type:
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={orderTypeFilter}
                        onChange={(_e, v) => { if (v) { setPage(0); setOrderTypeFilter(v) } }}
                        sx={{
                            '& .MuiToggleButton-root': {
                                fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, textTransform: 'none',
                                px: 2, py: 0.5, borderColor: BORDER, color: NAVY,
                                '&.Mui-selected': { bgcolor: PRIMARY_BLUE, color: WHITE, '&:hover': { bgcolor: PRIMARY_BLUE } },
                            },
                        }}
                    >
                        {ORDER_TYPES.map((t) => <ToggleButton key={t.value} value={t.value}>{t.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                </Box>
                <FormProvider methods={methods} onSubmit={handleSubmit(applyFilters)}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <RHFTextField name="reference" label="Order / Reference Number" size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <RHFTextField name="pickup_pincode" label="Pickup Pincode" size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <RHFTextField name="drop_pincode" label="Drop Pincode" size="small" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <RHFDateRangePickerField label="Created Date" maxDate={new Date()} />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={btnRowSx}>
                                <Button type="submit" variant="contained" size="small" sx={primaryBtnSx}
                                    startIcon={<SearchIcon />}>Filter</Button>
                                <Button variant="outlined" size="small" sx={secondaryBtnSx}
                                    startIcon={<RestartAltIcon />} onClick={onReset}>Reset</Button>
                                <Button variant="outlined" size="small" sx={secondaryBtnSx}
                                    startIcon={<FileDownloadOutlinedIcon />}
                                    onClick={fetchAllDataForExport} disabled={isExporting}>
                                    {isExporting ? 'Exporting...' : 'Export CSV'}
                                </Button>
                                <CSVLink data={exportData} headers={csvHeaders} filename={"unprocessed_shipments.csv"}
                                    className="hidden" target="_blank" ref={csvLink} />
                            </Box>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>

            {/* Data grid */}
            <Box sx={dataGridWrapSx}>
                <ProzoDataGrid
                    columns={columns}
                    hideFooterPagination={false}
                    autoHeight={true}
                    rows={data?.data || []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    filterMode={"server"}
                    rowCount={totalRows}
                    getRowId={(row) => row.id}
                />
            </Box>
            {(isLoading || isFetching) && <Loader />}
        </Box>
    )
}

export default withLayout(StuckOrders)
